<template>
  <div>
    <v-container class="rs-controls">
      <v-row dense>
        <v-col cols="4" v-for="(button, index) in buttons" :key="`button-${index}`">
          <SensorButton
            v-model="button.value"
            :conversion="button.conversion"
            :name="button.name"
            :initialValue="button.initialValue"
            @input="onSensorInput(button, button.platformIndex == null ? index : button.platformIndex, $event)"
          />
        </v-col>
        <v-col>
          <v-tooltip bottom>
            <template v-slot:activator="{ on: tooltip }">
              <span
                v-on="tooltip"
              >
              <v-checkbox
                v-model="isInverted"
                label="Invertiert"
                @change="cbIsInvertedChanged"
              ></v-checkbox>
              </span>
            </template>
            Im Regler ist beim Sensormodul die Option Invertiert aktiv<br /><br />
            Der Überschuss muss negativ eingestellt werden.
          </v-tooltip>
        </v-col>
      </v-row>
      <template v-if="bridge">
        <v-row v-for="(consumer, i) in bridge.consumers" :key="i">
          <v-col cols="4">
            <v-text-field
              v-model="consumer.maximumPower"
              label="Leistung am Ausgang"
            >
              <template v-slot:append>W</template>
            </v-text-field>
          </v-col>
          <v-col cols="4">
            <v-select
              label="Verbraucher"
              v-model="consumer.output"
              :items="controllerOutputs"
              return-object
              item-text="name"
            ></v-select>
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="consumer.activePower"
              label="Verbrauchte Leistung"
              :disabled="true"
            >
            </v-text-field>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </div>
</template>

<script>
/*
  js data ---> sendUartData() ---> VBusChannel ---> emit(uartDidTransmit)
*/
import { Bridge } from '@/bridges/Bridge.js';
import SensorButton from '@/components/SensorButton';

const Connection = require('resol-vbus/src/connection');
const Packet = require('resol-vbus/src/packet');

const RVM_VBUS_COMMAND_REQUEST_INFO = 0x0310;
const RVM_VBUS_COMMAND_RESPONSE_INFO = 0x0110;
const RVM_VBUS_COMMAND_REQUEST_CONFIG = 0x0220;
const RVM_VBUS_COMMAND_RESPONSE_CONFIG = 0x0120;
const RVM_VBUS_COMMAND_SET_CONTROL = 0x0230;
const RVM_VBUS_COMMAND_RESPONSE_CONTROL = 0x0130;


class PvsBridge extends Bridge {
  constructor(options) {
    super();
    this.vbusAddress = options.vbusAddress;
    this.buttons = options.buttons;
    this.connection = new Connection();
    this.connection.on('packet', this.handleSmPacket.bind(this));
    this.consumers = [];
    this.isInverted = options.isInverted || 0;

    this.sensorModule = {
      changeset: 0xabdc1234, //0x8a513b0c0539,
      versionNr: 100,
      errorMask: 0,
    };
  }

  calculateTap() {
    const powerW = this.buttons[0].value.value | 0;
    let powerEigenbedarfW = 0;
    for (const consumer of this.consumers) {
      if (consumer.type) {
        if (consumer.type === 256) { // Dutycycle
          consumer.activePower = consumer.maximumPower * (consumer.value / 100000);
        } else if (consumer.type === 4) { // Voltage
          consumer.activePower = consumer.maximumPower * (consumer.value / 100000);
        } else if (consumer.type === 2) { // Digital
          // Anpassung an Digitalwerte (0/1 vs 0/1000) unterschiedlicher Regler:
          consumer.activePower = consumer.maximumPower * (consumer.value ? 1 : 0);
        } else {
          consumer.activePower = 0;
          // console.log("`PVSensormodul.calculateTap()`: UNKNOWN output type");
           // TODO(robin) weitere typen prüfen!
        }
        powerEigenbedarfW += consumer.activePower;
      }
    }
    let tap = 0;
    if (this.isInverted) {
      tap = (powerW + powerEigenbedarfW).toFixed();
    } else {
      tap = (powerW - powerEigenbedarfW).toFixed();
    }
    return tap;
  }

  sendUartData(index, rxBuffer) {
    if (index === 0) {
      this.connection.write(rxBuffer);
    }
  }

  setInputValueForType(index, type, value) {
    const consumer = this.consumers.find(x => x.output && x.output.index === index);
    if (consumer) {
      consumer.type = type;
      consumer.value = value;
    }
  }

  handleSmPacket(packet) {
    let txPacket = null;
    const defaultHeaderV2 = {
      version: 2,
      seqNrConfig: 0,
      status: 0,
    };
    const headerToBuffer = (header, buffer, offset) => {
      buffer.writeUInt8(header.version, 0 + offset); // Version
      buffer.writeUInt8(header.seqNrConfig, 1 + offset); // Sequenznr
      buffer.writeUInt8(header.status, 2 + offset); // Zustand
      buffer.writeUInt8(0, 3 + offset); // nop
    };
    const s0ConfigToBuffer = (s0, buffer, offset) => {
      buffer.writeUInt16LE(s0.impulseTime, 0 + offset); // U16 Impulsdauer;
      buffer.writeUInt16LE(s0.impulsePause, 2 + offset); // U16 Pulspause;
    };
    const powerToBuffer = (power, buffer, offset) => {
      buffer.writeInt32LE(power.totalActivePower * -3125, 0 + offset); // I32 TotalActivePower;
      buffer.writeUInt32LE(power.phAVoltRms, 8 + offset); // I32    Ph_A_Volt_RMS;
      buffer.writeUInt32LE(power.phBVoltRms, 12 + offset); // I32    Ph_B_Volt_RMS;
      buffer.writeUInt32LE(power.phCVoltRms, 16 + offset); // I32    Ph_C_Volt_RMS;
      buffer.writeUInt32LE(power.phACurrentRms, 20 + offset); // I32    Ph_A_Current_RMS;
      buffer.writeUInt32LE(power.phBCurrentRms, 24 + offset); // I32    Ph_B_Current_RMS;
      buffer.writeUInt32LE(power.phCCurrentRms, 28 + offset); // I32    Ph_C_Current_RMS;
      buffer.writeUInt16LE(power.phAMeanPhAngle, 30 + offset); // I16    Ph_A_Mean_Ph_Angle;
      buffer.writeUInt16LE(power.phBMeanPhAngle, 32 + offset); // I16    Ph_B_Mean_Ph_Angle;
      buffer.writeUInt16LE(power.phCMeanPhAngle, 34 + offset); // I16    Ph_C_Mean_Ph_Angle;
      buffer.writeUInt16LE(power.phAVoltPhAngle, 36 + offset); // I16    Ph_A_Volt_Ph_Angle;
      buffer.writeUInt16LE(power.phBVoltPhAngle, 38 + offset); // I16    Ph_B_Volt_Ph_Angle;
      buffer.writeUInt16LE(power.phCVoltPhAngle, 40 + offset); // I16    Ph_C_Volt_Ph_Angle;
    };
    const bufferToHeader = (buffer, offset) => {
      const header = {
        version: buffer.readUInt8(0 + offset),
        seqNrConfig: buffer.readUInt8(1 + offset),
        status: buffer.readUInt8(2 + offset),
      };
      return header;
    };
    const bufferToS0Config = (buffer, offset) => {
      const s0 = {
        impulseTime: buffer.readUInt16LE(0 + offset),
        impulsePause: buffer.readUInt16LE(2 + offset),
      };
      return s0;
    };
    const bufferToS0Control = (buffer, offset) => {
      const s0 = {
        impulseCounter: buffer.readUInt16LE(0 + offset),
        sequenceNr: buffer.readUInt8(2 + offset),
        crc: buffer.readUInt8(2 + offset),
      };
      return s0;
    };
    switch (packet.command) {
      case RVM_VBUS_COMMAND_REQUEST_INFO: {
        const txFrameData = Buffer.alloc(4 * 3);
        headerToBuffer(defaultHeaderV2, txFrameData, 0);

        // console.log('REQUEST_INFO');
        txFrameData.writeUInt32LE(this.sensorModule.changeset, 4); // SW-ChangesetId
        txFrameData.writeUInt8(this.sensorModule.versionNr, 8); // SW-Version

        txPacket = new Packet({
          destinationAddress: packet.sourceAddress,
          sourceAddress: packet.destinationAddress,
          command: RVM_VBUS_COMMAND_RESPONSE_INFO,
          frameCount: 3,
          frameData: txFrameData,
        });
      }
      break;
      case RVM_VBUS_COMMAND_REQUEST_CONFIG: {
        const receivedHeader = bufferToHeader(packet.frameData, 0);
        const s0AConfig = bufferToS0Config(packet.frameData, 4);
        const s0BConfig = bufferToS0Config(packet.frameData, 8);
        // console.log('REQUEST_CONFIG', receivedHeader.version, packet.frameCount);
        if (receivedHeader.version === 1) {
          const txFrameData = Buffer.alloc(4 * 3);
          headerToBuffer({ ...defaultHeaderV2, seqNrConfig: receivedHeader.seqNrConfig }, txFrameData, 0);
          txPacket = new Packet({
            destinationAddress: packet.sourceAddress,
            sourceAddress: packet.destinationAddress,
            command: RVM_VBUS_COMMAND_RESPONSE_CONFIG,
            frameCount: 3,
            frameData: txFrameData,
          });
        } else if (receivedHeader.version === 2) {
          const txFrameData = Buffer.alloc(4 * 3);
          headerToBuffer({ ...defaultHeaderV2, seqNrConfig: receivedHeader.seqNrConfig }, txFrameData, 0);

          s0ConfigToBuffer(s0AConfig, txFrameData, 4); // S0-A
          s0ConfigToBuffer(s0BConfig, txFrameData, 8); // S0-B

          txPacket = new Packet({
            destinationAddress: packet.sourceAddress,
            sourceAddress: packet.destinationAddress,
            command: RVM_VBUS_COMMAND_RESPONSE_CONFIG,
            frameCount: 3,
            frameData: txFrameData,
          });
        }
      }
      break;
      case RVM_VBUS_COMMAND_SET_CONTROL: {
        const receivedHeader = bufferToHeader(packet.frameData, 0);
        // const led1 = packet.frameData.readUInt32LE(4);
        // const led2 = packet.frameData.readUInt32LE(8);
        // const led3 = packet.frameData.readUInt32LE(12);
        // const led4 = packet.frameData.readUInt32LE(16);
        const s0AControl = bufferToS0Control(packet.frameData, 20);
        const s0BControl = bufferToS0Control(packet.frameData, 24);
        // console.log('SET_CONTROL', receivedHeader.version, packet.frameCount);
        if (receivedHeader.version === 1) {
          const txFrameData = Buffer.alloc(4 * 2);
          headerToBuffer({ ...defaultHeaderV2, seqNrConfig: receivedHeader.seqNrConfig }, txFrameData, 0);
          txFrameData.writeInt32LE(this.calculateTap() * -3125, 4); // I32 TotalActivePower;

          txPacket = new Packet({
            destinationAddress: packet.sourceAddress,
            sourceAddress: packet.destinationAddress,
            command: RVM_VBUS_COMMAND_RESPONSE_CONTROL,
            frameCount: 2,
            frameData: txFrameData,
          });
        } else if (receivedHeader.version === 2) {
          const txFrameData = Buffer.alloc(4 * 12);
          headerToBuffer({ ...defaultHeaderV2, seqNrConfig: receivedHeader.seqNrConfig }, txFrameData, 0);
          powerToBuffer({
            totalActivePower: this.calculateTap(),
            phAVoltRms: 0,
            phBVoltRms: 0,
            phCVoltRms: 0,
            phACurrentRms: 0,
            phBCurrentRms: 0,
            phCCurrentRms: 0,
            phAMeanPhAngle: 0,
            phBMeanPhAngle: 0,
            phCMeanPhAngle: 0,
            phAVoltPhAngle: 0,
            phBVoltPhAngle: 0,
            phCVoltPhAngle: 0,
          }, txFrameData, 4);
          txFrameData.writeInt16LE(s0AControl.impulseCounter, 8); // I16 ImpulsZaehler_S0_A;
          txFrameData.writeInt16LE(s0BControl.impulseCounter, 10); // I16 ImpulsZaehler_S0_B;

          txPacket = new Packet({
            destinationAddress: packet.sourceAddress,
            sourceAddress: packet.destinationAddress,
            command: RVM_VBUS_COMMAND_RESPONSE_CONTROL,
            frameCount: 3,
            frameData: txFrameData,
          });
        }
      }
      break;
    }

    if (txPacket) {
      const txBuffer = txPacket.toLiveBuffer();
      for (let i = 0; i < txBuffer.length; i += 1) {
        this.emit('uartDidTransmit', 0, txBuffer[i]);
      }
    }
  }

}

export default {
  props: [
    'vbusAddress',
    'controllerOutputs',
    'defaultValues',
  ],

  components: {
    SensorButton,
  },

  data: () => ({
    bridge: null,
    intervalId: 0,
    isInverted: 0,
    relaisState: [],

    buttons: [
      { conversion: 'rawPower', value: null, initialValue: 0, name: 'Verfügbar' },
    ],

    uarts: [
      { name: 'UART0', kind: 'VBus', label: 'Intern' },
    ],
  }),

  methods: {
    async prepareDevice() {
      this.bridge = new PvsBridge({
        vbusAddress: this.vbusAddress,
        buttons: this.buttons,
        isInverted: this.isInverted,
      });
      this.intervalId = setInterval(this.intervalHandler.bind(this), 1000);

      if (this.defaultValues) {
        if (this.defaultValues.maximumValue) {
          // SensorButton Maximum anpassen
          this.$children[0].customizeConversion('rawPower', {maximumValue: this.defaultValues.maximumValue});
        }
        if (this.defaultValues.minimumValue) {
          // SensorButton Minimum anpassen
          this.$children[0].customizeConversion('rawPower', {minimumValue: this.defaultValues.minimumValue});
        }
      }

      for (let i = 0; i < 5; i += 1) {
        this.bridge.consumers.push({
          maximumPower: 0,
          activePower: 0,
          output: null,
          type: 0,
          value: 0,
        });
      }
    },

    startDevice() {},

    restartDevice() {},

    onSensorInput(/*button, index, value*/) {},

    intervalHandler() {},

    cbIsInvertedChanged() {
      if (this.bridge) {
        this.bridge.isInverted = this.isInverted;
      }
    }
  },
};
</script>

<style>

</style>
